// libraries
import type { AnyFSA } from '@makemydeal/dr-browser-utils';
import type { BaggageEntry } from '@makemydeal/dr-dash-bff-types';
import { MV_AUTO_SELECT_STATE_BAGGAGE_NAME } from '@makemydeal/dr-dash-types';
import { actions as offerReduxActionTypes } from '@makemydeal/dr-offer-redux';
import { FINANCE, LEASE } from '@makemydeal/dr-platform-types';
import type { OfferType } from '@makemydeal/dr-platform-types';
import type { DashMiddleware, DashNext, DashStore } from '@makemydeal/dr-shared-store';
import { featureToggleSelectors, initActionTypes } from '@makemydeal/dr-shared-store';

// actions
import {
    MILEAGE_COST_OPTIONS_OVERRIDE_FIELDS,
    clearManualFieldOverrides,
    hydrateManualFieldOverrides,
    setAutoSelectChecked,
    setAutoSelectDealerDefaults
} from '../actions/manualFieldOverridesActionCreators';
import { syncedMaxResidualizedMsrpOverride } from '../actions/offerActionCreators';
import { updateCurrentCalculationEndpoint } from '../actions/offerRedux';
import { CALC_ENDPOINT_OWNED_BY_DISPATCHER_META } from '../actions/programOrchestrationActionCreators';
import { PAYMENT_RECEIVED } from '../actionTypes/deskingActionTypes';
import { isMainPaymentTrigger } from '../utils/paymentActionUtils';

// constants / selectors
import { CALC_ENDPOINTS } from '../constants';
import { isFnIDealMode } from '../selectors/dealModeSelectors';
import { getIsAutoSelectChecked, getManualFieldOverrides } from '../selectors/manualFieldOverridesSelectors';
import * as offerReduxSelectors from '../selectors/offerRedux';

const { INIT_SUCCESS } = initActionTypes;

// Cash has no Auto Select checkbox, and Balloon is not supported.
const SEEDED_OFFER_TYPES: ReadonlySet<OfferType> = new Set<OfferType>([FINANCE, LEASE]);

const restoreDealerMaxResidualizedMsrp = (store: DashStore, state: ReturnType<DashStore['getState']>): void => {
    if (offerReduxSelectors.getCurrentOfferType(state) !== LEASE) return;
    if (offerReduxSelectors.getOfferDetails(state, LEASE).maxResidualizedMsrp != null) return;

    const term = offerReduxSelectors.getSelectedTermByOfferType(state, LEASE);
    const maxResidualizedMsrp = term?.maxResidualizedMsrp;
    if (maxResidualizedMsrp == null || maxResidualizedMsrp <= 0) return;

    store.dispatch(syncedMaxResidualizedMsrpOverride(maxResidualizedMsrp, term.maxResidualizedMsrpApplied ?? false));
};

const MILEAGE_OVERRIDE_KEYS: string[] = [...MILEAGE_COST_OPTIONS_OVERRIDE_FIELDS];

const clearMileageOverridesRepricedByProgram = (store: DashStore, action: AnyFSA): void => {
    if (action.meta?.isStalePaymentResponse || !isMainPaymentTrigger(action.meta?.originalAction) || !action.payload?.[LEASE])
        return;

    const state = store.getState();
    if (offerReduxSelectors.getCurrentOfferType(state) !== LEASE) return;
    const pricedOn = action.meta?.requestCalculationEndpoint ?? offerReduxSelectors.getCalculationEndpointByOfferType(state, LEASE);
    if (pricedOn !== CALC_ENDPOINTS.programQuotes) return;
    if (!getManualFieldOverrides(state).some((field) => MILEAGE_OVERRIDE_KEYS.includes(field))) return;

    store.dispatch(clearManualFieldOverrides(MILEAGE_OVERRIDE_KEYS));
};

// Restores the user's Auto Select selection from baggage, and seeds the endpoint from it.
export const middleware: DashMiddleware = (store: DashStore) => (next: DashNext) => (action: AnyFSA) => {
    // Read before the reducer swaps the deal in, so the seed below can tell a new deal from a re-init. Safe to
    // compare against the post-`next` value because `dealXgId` is written by a plain reducer on this same
    // action (`offer-redux/src/reducers/integrationAndExternalSystems.ts`), so it has landed by then.
    const previousDealXgId = action.type === INIT_SUCCESS ? offerReduxSelectors.getDealXgId(store.getState()) : undefined;

    next(action);

    if (action.type === PAYMENT_RECEIVED) {
        clearMileageOverridesRepricedByProgram(store, action);
        return;
    }

    if (action.type === offerReduxActionTypes.UPDATED_OFFER_TYPE) {
        const state = store.getState();
        if (!featureToggleSelectors.isImprovedAutoSelectEnabled(state)) return;

        // A forced-UPQ dealer outranks any selection, and these do not end on program-quotes.
        if (!action.meta?.middleware?.payment) return;
        if (offerReduxSelectors.isFlexBuyPayment(state)) return;
        if (offerReduxSelectors.getDealerForcedUpq(state)) return;
        if (isFnIDealMode(state)) return;

        const offerType = offerReduxSelectors.getCurrentOfferType(state);
        if (!SEEDED_OFFER_TYPES.has(offerType)) return;
        if (getIsAutoSelectChecked(state, offerType) !== undefined) return;

        // A transition whose dispatcher owns the endpoint has already set it, so record only where it
        // actually landed on program-quotes. Staying silent when it landed on user-program-quotes leaves the
        // dealer default answering, so nothing is written that a later transition would be unable to correct.
        // Recording matters because it is the only answer that survives a fallback: the endpoint moves off
        // program-quotes, and without a record the checkbox unticks itself and the term message disappears.
        //
        // Reading the endpoint here assumes the dispatcher sets it BEFORE dispatching. That holds for every
        // marked transition except cash-in, where `handleCashDealTransition` deliberately runs after the
        // dispatch so the payment grid's Cash guard fires first. Cash-in never reaches this line only
        // because CASH is absent from SEEDED_OFFER_TYPES above - an incidental shield, not an invariant of
        // the marker. Adding CASH to that set, or moving the guard below this, silently reads a stale
        // endpoint.
        if (
            action.meta[CALC_ENDPOINT_OWNED_BY_DISPATCHER_META] &&
            offerReduxSelectors.getCalculationEndpointByOfferType(state, offerType) !== CALC_ENDPOINTS.programQuotes
        ) {
            return;
        }

        store.dispatch(setAutoSelectChecked(offerType, true));
        return;
    }

    // MV re-dispatches INIT_SUCCESS from INIT_PENCIL_SUCCESS with the same payload; handling only this one
    // avoids a double dispatch and runs after the reducer derives the endpoint from the deal.
    if (action.type !== INIT_SUCCESS) return;

    const state = store.getState();
    // Explicit Lender P1 persists overridden fields too, so the hydrate below has to run for it as well or
    // the acquisition fee's override indicator would not survive a reload. The endpoint seeding further
    // down is Improved Auto Select's alone and is skipped for P1 — see the guard above that dispatch.
    const isImprovedAutoSelectEnabled = featureToggleSelectors.isImprovedAutoSelectEnabled(state);
    if (
        !isImprovedAutoSelectEnabled &&
        !featureToggleSelectors.isExplicitLenderP1Enabled(state) &&
        !featureToggleSelectors.isAspPaymentGridRedesignEnabled(state)
    )
        return;

    const baggage = action.payload?.baggage;
    const entry = Array.isArray(baggage)
        ? baggage.find(({ name }: BaggageEntry) => name === MV_AUTO_SELECT_STATE_BAGGAGE_NAME)
        : undefined;
    const storedAutoSelectState = entry?.value?.content as { checked?: unknown; overriddenFields?: unknown } | undefined;
    const { checked, overriddenFields } = storedAutoSelectState ?? {};

    // Hydrate on every load, including when the deal has no stored fields: the slice would otherwise keep
    // whatever the previously opened deal recorded. Must stay above the forced-UPQ return below, which
    // governs the endpoint only.
    const hydratedFields = Array.isArray(overriddenFields) ? (overriddenFields as string[]) : [];
    store.dispatch(hydrateManualFieldOverrides(hydratedFields));

    if (featureToggleSelectors.isExplicitLenderP1Enabled(state) && hydratedFields.includes('maxResidualizedMsrp')) {
        restoreDealerMaxResidualizedMsrp(store, state);
    }

    // Everything below restores the Auto Select selection, seeds its dealer defaults, and derives the
    // endpoint from it — all Improved Auto Select's behaviour. Explicit Lender P1 wanted the hydrate above
    // and nothing else.
    if (!isImprovedAutoSelectEnabled) return;

    const dealerForcesUpq = offerReduxSelectors.getDealerForcedUpq(state);
    store.dispatch(
        setAutoSelectDealerDefaults(
            [...SEEDED_OFFER_TYPES].reduce<Partial<Record<OfferType, boolean>>>((defaults, seededOfferType) => {
                defaults[seededOfferType] =
                    !dealerForcesUpq &&
                    offerReduxSelectors.getCalculationEndpointByOfferType(state, seededOfferType) === CALC_ENDPOINTS.programQuotes;
                return defaults;
            }, {})
        )
    );

    // A dealer forcing user-program-quotes outranks the stored selection.
    const storedChecked = !dealerForcesUpq && typeof checked === 'boolean' ? checked : undefined;

    // A load only knows the deal type being opened, so a new deal replaces the whole map instead of merging.
    // `dealXgId` is `''` before a deal lands and stays `''` for a deal with no `dealExchangeDealId`, so
    // equality cannot prove two loads are the same deal. Seed whenever either side is blank: re-seeding a
    // re-init costs the user's in-session choice on a deal that has no id, while the other direction would let
    // the next deal inherit this one's selection, which is what this guard exists to stop.
    const currentDealXgId = offerReduxSelectors.getDealXgId(state);
    const isDifferentDeal = !currentDealXgId?.trim() || !previousDealXgId?.trim() || currentDealXgId !== previousDealXgId;

    const offerType = offerReduxSelectors.getCurrentOfferType(state);

    if (storedChecked !== undefined) {
        store.dispatch(setAutoSelectChecked(offerType, storedChecked, isDifferentDeal));
    } else if (isDifferentDeal || getIsAutoSelectChecked(store.getState(), offerType) === undefined) {
        // Nothing stored: seed from the endpoint, which already carries the dealer admin default. For the
        // same deal only when nothing is recorded yet, since INIT_SUCCESS also arrives mid-session from a
        // deal template and re-seeding would discard the user's choice.
        store.dispatch(
            setAutoSelectChecked(
                offerType,
                offerReduxSelectors.getCalculationEndpointByOfferType(state, offerType) === CALC_ENDPOINTS.programQuotes,
                isDifferentDeal
            )
        );
    }

    if (dealerForcesUpq) return;

    // Absent checked: the init path already applies the dealer admin default, so don't derive it twice.
    if (typeof checked !== 'boolean') return;

    // P1 hides the checkbox, so a stale `checked` must not override the restored endpoint (DE773849).
    if (featureToggleSelectors.isExplicitLenderP1Enabled(state)) return;

    // Seed the endpoint from the stored selection. No payment is requested, and the grid populates on load
    // itself, so no rebuild.
    store.dispatch(
        updateCurrentCalculationEndpoint(
            checked ? CALC_ENDPOINTS.programQuotes : CALC_ENDPOINTS.userProgramQuotes,
            offerReduxSelectors.getCurrentOfferType(state),
            { skipGridRebuild: true }
        )
    );
};
